// Dialog de confirmação reusável — substitui `window.confirm()` nativo
// pra ações destrutivas (remover dia, remover categoria, etc).
//
// Por que evitar `confirm()`:
//  - Visualmente ruim em iOS Safari mobile
//  - Bloqueante, sem suporte a undo / detalhes
//  - Não dá pra estilizar nem testar via Playwright
//
// Uso típico:
//   const [confirming, setConfirming] = useState<string | null>(null);
//   ...
//   <ConfirmDialog
//     open={confirming != null}
//     onOpenChange={(o) => !o && setConfirming(null)}
//     title="Remover dia?"
//     description={`O dia "${name}" e todos os segmentos serão apagados.`}
//     confirmLabel="Remover"
//     destructive
//     onConfirm={() => { remove.mutate(confirming!); setConfirming(null); }}
//   />

import * as Dialog from '@radix-ui/react-dialog';
import { Button } from './button';
import { commonT } from '@/i18n';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  onConfirm: () => void;
  /// Campos extras entre a descrição e os botões — pra confirmação que
  /// precisa COLETAR algo, não só perguntar sim/não (anular boia pede motivo
  /// e observação, #929). Sem isto, cada ação dessas traria um shell de modal
  /// próprio e a animação/safe-area/breakpoint passariam a divergir por tela.
  children?: React.ReactNode;
  /// Trava o botão de confirmar enquanto o que `children` coleta não é
  /// válido. Cancelar continua sempre disponível.
  confirmDisabled?: boolean;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  destructive,
  onConfirm,
  children,
  confirmDisabled,
}: ConfirmDialogProps) {
  const common = commonT();
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/50 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        {/* Animação top-down em ambos breakpoints: mobile vira top sheet
            (encostado no topo, desce); desktop é modal centralizado que
            entra deslizando de cima. User feedback 2026-05-29 — bottom
            sheet conflitava com sidebar do live admin à direita. */}
        <Dialog.Content
          className="
            fixed z-50 bg-background shadow-xl
            inset-x-0 top-0 rounded-b-xl pt-[env(safe-area-inset-top)]
            md:inset-auto md:left-1/2 md:top-[15%] md:-translate-x-1/2
            md:w-full md:max-w-sm md:rounded-lg md:pt-0
            data-[state=open]:animate-in data-[state=open]:slide-in-from-top-4
          "
        >
          <div className="space-y-3 p-4">
            <Dialog.Title className="text-base font-semibold">{title}</Dialog.Title>
            {description && (
              <Dialog.Description className="text-sm text-muted-foreground">
                {description}
              </Dialog.Description>
            )}
            {children}
            <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="w-full sm:w-auto"
              >
                {cancelLabel ?? common.cancel}
              </Button>
              <Button
                type="button"
                variant={destructive ? 'destructive' : 'default'}
                disabled={confirmDisabled}
                onClick={() => {
                  onConfirm();
                  onOpenChange(false);
                }}
                className="w-full sm:w-auto"
              >
                {confirmLabel ?? common.confirm}
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
